import { Leaf, Waves, MapPin, Users } from "lucide-react"; // Trocámos Globe por Waves
import { useLanguage } from "@/context/LanguageContext";
import { useInView } from "@/hooks/useInView";

// O Waves ocupa agora a segunda posição (índice 1)
const FEATURE_ICONS = [Leaf, Waves, MapPin, Users];

export default function About() {
  const { t } = useLanguage();
  const [ref, inView] = useInView();
  
  const features = t("about.features");
  const displayFeatures = Array.isArray(features) ? features : [];

  return (
    <section id="about" className="py-24 md:py-32 bg-[#FDFCF8]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          ref={ref}
          className={`transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          {/* Badge */}
          <div className="flex items-center gap-3 mb-6">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#3A4D39] font-manrope">
              {t("about.badge")}
            </span>
            <div className="flex-1 h-px bg-[#3A4D39]/20" />
          </div>

          {/* Header */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-20">
            <div>
              <h2 className="font-playfair text-4xl md:text-5xl font-normal text-[#1F2937] leading-tight mb-8">
                {t("about.title")}
              </h2>
              <blockquote className="text-xl font-playfair italic text-[#D4A373] mb-8 pl-5 border-l-2 border-[#D4A373]">
                "{t("about.quote")}"
              </blockquote>
            </div>
            <div className="space-y-5">
              <p className="text-base font-manrope font-light text-[#4B5563] leading-relaxed">{t("about.text1")}</p>
              <p className="text-base font-manrope font-light text-[#4B5563] leading-relaxed">{t("about.text2")}</p>
              <p className="text-base font-manrope font-light text-[#4B5563] leading-relaxed">{t("about.text3")}</p>
            </div>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
            {displayFeatures.map((feat, i) => {
              const Icon = FEATURE_ICONS[i] || Leaf;
              return (
                <div
                  key={i}
                  className="p-7 bg-white rounded-2xl border border-stone-100 hover:shadow-lg hover:border-[#D4A373]/30 transition-all duration-300 group"
                  data-testid={`about-feature-${i}`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="w-10 h-10 rounded-xl bg-[#3A4D39]/10 flex items-center justify-center mb-4 group-hover:bg-[#3A4D39]/20 transition-colors">
                    <Icon className="w-5 h-5 text-[#3A4D39]" />
                  </div>
                  <h3 className="font-playfair text-lg font-medium text-[#1F2937] mb-2">{feat.title}</h3>
                  <p className="text-sm font-manrope font-light text-[#6B7280] leading-relaxed">{feat.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Hosts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="flex items-start gap-6 p-8 bg-[#3A4D39] rounded-2xl text-white">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#D4A373] mb-2 font-manrope">
                  {t("about.hostsLabel")}
                </p>
                <h3 className="font-playfair text-2xl font-medium mb-3">{t("about.hostsName")}</h3>
                <p className="text-white/80 font-manrope font-light leading-relaxed italic">
                  "{t("about.hostsQuote")}"
                </p>
              </div>
            </div>
            
            <div className="overflow-hidden rounded-2xl shadow-sm border border-stone-100 h-[280px]">
              <a href="https://eco.yourholiday.pt/pt-pt/" target="_blank" rel="noopener noreferrer" className="block w-full h-full">
                <img
                  src="/d83d6aef-bee2-405e-9578-c0e210c9244c.jpg"
                  alt="Quinta das Cantigas — Eco Resort"
                  className="w-full h-full object-cover object-left hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
